import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { 
  getAdminStats, getAdminListings, getAdminUsers, 
  toggleFeatured, verifyListing, updateUserRole,
  getContactMessages, getPartnershipRequests,
  getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost,
  getAdminAds, createAd, deleteAd,
  exportUsersPdf, exportFullBackup, exportImagesZip, restoreBackup,
  approveListing, rejectListing, deleteUser, restoreUser,
  adminDeleteListing, getAdminUserDetail,
  getAnalyticsOverview, exportAnalytics,
  runSecurityAudit, cleanupTestData, prepareProduction,
  getCities, getNeighborhoods, getPendingNeighborhoods, approveNeighborhood, rejectNeighborhood, createNeighborhood, deleteNeighborhood
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Home, Users, MapPin, MessageSquare, Handshake,
  Loader2, Star, StarOff, CheckCircle, Eye, FileText, Megaphone, Plus, Trash2, Edit, Download, Upload, FileDown, Database, Image, CheckCheck, XCircle, Mail, Phone, MessageCircle, UserX, ArrowLeft, BarChart3, TrendingUp, Shield, AlertTriangle, Rocket
} from 'lucide-react';

const AdminDashboardPage = () => {
  const { t } = useLanguage();
  const location = useLocation();

  const navItems = [
    { path: '/admin', label: t('statistics'), icon: LayoutDashboard },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/listings', label: t('manageListings'), icon: Home },
    { path: '/admin/users', label: t('manageUsers'), icon: Users },
    { path: '/admin/neighborhoods', label: 'Quartiers', icon: MapPin },
    { path: '/admin/blog', label: 'Blog', icon: FileText },
    { path: '/admin/ads', label: 'Publicités', icon: Megaphone },
    { path: '/admin/backup', label: 'Backup', icon: Download },
    { path: '/admin/security', label: 'Sécurité', icon: Shield },
    { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-8" data-testid="admin-title">
          {t('admin')}
        </h1>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive ? "default" : "outline"}
                  className="gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>

        <Routes>
          <Route index element={<StatsView />} />
          <Route path="analytics" element={<AnalyticsView />} />
          <Route path="listings" element={<ListingsView />} />
          <Route path="users" element={<UsersView />} />
          <Route path="users/:userId" element={<UserDetailView />} />
          <Route path="neighborhoods" element={<NeighborhoodsView />} />
          <Route path="blog" element={<BlogManagement />} />
          <Route path="ads" element={<AdsManagement />} />
          <Route path="backup" element={<BackupManagement />} />
          <Route path="security" element={<SecurityView />} />
          <Route path="messages" element={<MessagesView />} />
        </Routes>
      </div>
    </div>
  );
};

const StatsView = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await getAdminStats();
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-cards">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Home className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('totalListings')}</p>
                <p className="text-2xl font-bold">{stats?.total_listings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('activeListings')}</p>
                <p className="text-2xl font-bold">{stats?.active_listings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={stats?.pending_listings > 0 ? "border-yellow-400 bg-yellow-50/50" : ""}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">En attente</p>
                <p className="text-2xl font-bold">{stats?.pending_listings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('totalUsers')}</p>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <Star className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('featuredListings')}</p>
                <p className="text-2xl font-bold">{stats?.featured_listings || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cities Stats */}
      {stats?.cities_stats?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Annonces par ville</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.cities_stats.map((city, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{city._id || 'Non défini'}</span>
                      <span className="text-muted-foreground">{city.count} annonces</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${(city.count / (stats.cities_stats[0]?.count || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Analytics View - Comprehensive analytics for investors
const AnalyticsView = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await getAnalyticsOverview(period);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await exportAnalytics(format);
      const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `roomia_analytics_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with Period Selector and Export */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Analytics & Statistiques
          </h2>
          <p className="text-muted-foreground">Données pour présentation aux investisseurs</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
              <SelectItem value="90d">90 derniers jours</SelectItem>
              <SelectItem value="all">Tout</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => handleExport('csv')}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => handleExport('json')}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
            Export JSON
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-xs text-muted-foreground">Utilisateurs</p>
                <p className="text-2xl font-bold">{analytics?.summary?.total_users || 0}</p>
                <p className="text-xs text-green-600">+{analytics?.summary?.new_users_period || 0} cette période</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-xs text-muted-foreground">Annonces actives</p>
                <p className="text-2xl font-bold">{analytics?.summary?.active_listings || 0}</p>
                <p className="text-xs text-green-600">+{analytics?.summary?.new_listings_period || 0} nouvelles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Eye className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-xs text-muted-foreground">Vues totales</p>
                <p className="text-2xl font-bold">{analytics?.summary?.total_views?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-xs text-muted-foreground">Clics WhatsApp</p>
                <p className="text-2xl font-bold">{analytics?.summary?.total_whatsapp_clicks?.toLocaleString() || 0}</p>
                <p className="text-xs text-emerald-600">+{analytics?.summary?.whatsapp_clicks_period || 0} cette période</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-xs text-muted-foreground">Events trackés</p>
                <p className="text-2xl font-bold">{analytics?.summary?.events_count?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Listings by Views */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Top 10 Annonces (Vues)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.top_listings?.map((listing, index) => (
                <div key={listing.listing_id} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{listing.title}</p>
                    <p className="text-xs text-muted-foreground">{listing.city}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{listing.views?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">vues</p>
                  </div>
                </div>
              ))}
              {(!analytics?.top_listings || analytics.top_listings.length === 0) && (
                <p className="text-center text-muted-foreground py-4">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Contacted Listings (WhatsApp) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />
              Top 10 Annonces (WhatsApp)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.top_contacted?.map((listing, index) => (
                <div key={listing.listing_id} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center text-sm font-bold text-green-600">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{listing.title}</p>
                    <p className="text-xs text-muted-foreground">{listing.city}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{listing.whatsapp_clicks?.toLocaleString() || 0}</p>
                    <p className="text-xs text-muted-foreground">contacts</p>
                  </div>
                </div>
              ))}
              {(!analytics?.top_contacted || analytics.top_contacted.length === 0) && (
                <p className="text-center text-muted-foreground py-4">Aucune donnée (les clics WhatsApp seront trackés)</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Cities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              Villes les plus populaires
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.top_cities?.map((city, index) => (
                <div key={city._id} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center text-sm font-bold text-blue-600">
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium">{city._id || 'Non défini'}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{city.count} annonces</span>
                      <span>{city.views?.toLocaleString() || 0} vues</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Owners */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              Propriétaires les plus performants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.top_owners?.map((owner, index) => (
                <div key={owner._id} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-500/10 flex items-center justify-center text-sm font-bold text-purple-600">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{owner.name}</p>
                    <p className="text-xs text-muted-foreground">{owner.email}</p>
                  </div>
                  <div className="text-right text-xs">
                    <p><span className="font-bold">{owner.listings_count}</span> annonces</p>
                    <p><span className="font-bold text-primary">{owner.total_views?.toLocaleString()}</span> vues</p>
                    <p><span className="font-bold text-green-600">{owner.total_whatsapp || 0}</span> WhatsApp</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Neighborhoods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            Quartiers les plus populaires
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics?.top_neighborhoods?.map((n, index) => (
              <div key={index} className="p-3 bg-secondary/50 rounded-lg">
                <p className="font-medium">{n._id?.neighborhood || 'Non défini'}</p>
                <p className="text-xs text-muted-foreground">{n._id?.city}</p>
                <div className="flex items-center gap-4 mt-1 text-sm">
                  <span className="text-primary font-bold">{n.views?.toLocaleString()} vues</span>
                  <span className="text-muted-foreground">{n.count} annonces</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Auth Providers Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Méthodes d'inscription</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.auth_providers?.map((provider) => (
                <div key={provider._id} className="flex items-center justify-between">
                  <span className="capitalize">{provider._id === 'email' ? 'Email/Mot de passe' : provider._id}</span>
                  <Badge variant="secondary">{provider.count} utilisateurs</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Types de logements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.listing_types?.map((type) => (
                <div key={type._id} className="flex items-center justify-between">
                  <span className="capitalize">{type._id}</span>
                  <Badge variant="outline">{type.count} annonces</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ListingsView = () => {
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadListings();
  }, [filter]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const res = await getAdminListings(params);
      setListings(res.data);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (listingId) => {
    try {
      const res = await toggleFeatured(listingId);
      setListings(prev => prev.map(l => 
        l.listing_id === listingId 
          ? { ...l, is_featured: res.data.is_featured }
          : l
      ));
      toast.success('Mise à jour réussie');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleVerify = async (listingId) => {
    try {
      await verifyListing(listingId);
      setListings(prev => prev.map(l => 
        l.listing_id === listingId 
          ? { ...l, is_verified: true }
          : l
      ));
      toast.success('Annonce vérifiée');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleApprove = async (listingId) => {
    try {
      await approveListing(listingId);
      setListings(prev => prev.map(l => 
        l.listing_id === listingId 
          ? { ...l, is_active: true, is_pending: false }
          : l
      ));
      toast.success('Annonce approuvée et publiée');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleReject = async (listingId) => {
    try {
      await rejectListing(listingId);
      setListings(prev => prev.map(l => 
        l.listing_id === listingId 
          ? { ...l, is_active: false, is_pending: false }
          : l
      ));
      toast.success('Annonce rejetée');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteListing = async (listingId, listingTitle) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement l'annonce "${listingTitle}" ?`)) return;
    
    try {
      await adminDeleteListing(listingId);
      setListings(prev => prev.filter(l => l.listing_id !== listingId));
      toast.success('Annonce supprimée définitivement');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="active">Actives</SelectItem>
            <SelectItem value="inactive">Inactives</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Ville</TableHead>
              <TableHead>Prix</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listings.map((listing) => (
              <TableRow key={listing.listing_id}>
                <TableCell className="font-medium max-w-xs truncate">
                  {listing.title}
                </TableCell>
                <TableCell>{listing.city}</TableCell>
                <TableCell>{listing.price_per_night?.toLocaleString()} FCFA</TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {listing.is_pending ? (
                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                        En attente
                      </Badge>
                    ) : (
                      <Badge variant={listing.is_active ? "default" : "secondary"}>
                        {listing.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    )}
                    {listing.is_featured && <Badge variant="outline">Featured</Badge>}
                    {listing.is_verified && <Badge className="bg-green-500">Vérifié</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Link to={`/listings/${listing.listing_id}`}>
                      <Button variant="ghost" size="sm" title="Voir">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    {listing.is_pending ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleApprove(listing.listing_id)}
                          title="Approuver"
                        >
                          <CheckCheck className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleReject(listing.listing_id)}
                          title="Rejeter"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleFeatured(listing.listing_id)}
                          title={listing.is_featured ? "Retirer des featured" : "Mettre en featured"}
                        >
                          {listing.is_featured ? (
                            <StarOff className="h-4 w-4" />
                          ) : (
                            <Star className="h-4 w-4" />
                          )}
                        </Button>
                        {!listing.is_verified && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleVerify(listing.listing_id)}
                            title="Vérifier"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {!listing.is_active && !listing.is_pending && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleApprove(listing.listing_id)}
                            title="Activer"
                          >
                            <CheckCheck className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    )}
                    {/* Delete Button - Always visible */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteListing(listing.listing_id, listing.title)}
                      title="Supprimer définitivement"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

const UsersView = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await getAdminUsers();
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, role: newRole } : u
      ));
      toast.success('Rôle mis à jour');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userName}" ?`)) return;
    
    try {
      await deleteUser(userId);
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      toast.success('Utilisateur supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Téléphone</TableHead>
            <TableHead>Rôle</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.user_id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.phone || '-'}</TableCell>
              <TableCell>
                <Select
                  value={user.role}
                  onValueChange={(v) => handleRoleChange(user.user_id, v)}
                  disabled={user.role === 'superadmin'}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(user.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Link to={`/admin/users/${user.user_id}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Voir détails"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                  {user.role !== 'superadmin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteUser(user.user_id, user.name)}
                      title="Supprimer l'utilisateur"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

// User Detail View - Shows user info and their listings
const UserDetailView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    try {
      const res = await getAdminUserDetail(userId);
      setUserData(res.data);
    } catch (error) {
      console.error('Error loading user:', error);
      toast.error('Erreur lors du chargement des données utilisateur');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async (listingId, title) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'annonce "${title}" ?`)) return;
    
    try {
      await adminDeleteListing(listingId);
      setUserData(prev => ({
        ...prev,
        listings: prev.listings.filter(l => l.listing_id !== listingId),
        listings_count: prev.listings_count - 1
      }));
      toast.success('Annonce supprimée');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Utilisateur non trouvé</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
          Retour à la liste
        </Button>
      </div>
    );
  }

  const { user, listings, reviews, listings_count, reviews_count } = userData;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" className="gap-2" onClick={() => navigate('/admin/users')}>
        <ArrowLeft className="h-4 w-4" />
        Retour aux utilisateurs
      </Button>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-primary">{user.name?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">{user.name}</h2>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Mail className="h-4 w-4" /> {user.email}</span>
                {user.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" /> {user.phone}</span>}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{listings_count}</p>
              <p className="text-sm text-muted-foreground">Annonces</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-2xl font-bold text-primary">{reviews_count}</p>
              <p className="text-sm text-muted-foreground">Avis</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <Badge className="text-lg">{user.role}</Badge>
              <p className="text-sm text-muted-foreground mt-1">Rôle</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <p className="text-lg font-medium">{new Date(user.created_at).toLocaleDateString('fr-FR')}</p>
              <p className="text-sm text-muted-foreground">Inscrit le</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User's Listings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Annonces de l'utilisateur ({listings_count})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {listings.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Cet utilisateur n'a pas d'annonces</p>
          ) : (
            <div className="space-y-4">
              {listings.map((listing) => (
                <div key={listing.listing_id} className="flex items-center gap-4 p-4 border rounded-lg">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{listing.title}</h4>
                    <p className="text-sm text-muted-foreground">{listing.city}, {listing.neighborhood}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={listing.is_active ? "default" : "secondary"} className="text-xs">
                        {listing.is_active ? 'Active' : listing.is_pending ? 'En attente' : 'Inactive'}
                      </Badge>
                      <span className="text-sm font-medium">{listing.price_per_night?.toLocaleString()} FCFA/nuit</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Eye className="h-3 w-3" /> {listing.views || 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/listings/${listing.listing_id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteListing(listing.listing_id, listing.title)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User's Reviews */}
      {reviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Avis laissés par l'utilisateur ({reviews_count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.review_id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <Badge variant={review.is_approved ? "default" : "secondary"}>
                      {review.is_approved ? 'Approuvé' : 'En attente'}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground">{review.comment}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(review.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MessagesView = () => {
  const [messages, setMessages] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [messagesRes, partnershipsRes] = await Promise.all([
        getContactMessages(),
        getPartnershipRequests()
      ]);
      setMessages(messagesRes.data);
      setPartnerships(partnershipsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="contact" className="space-y-6">
      <TabsList>
        <TabsTrigger value="contact">Messages Contact ({messages.length})</TabsTrigger>
        <TabsTrigger value="partnership">Partenariats ({partnerships.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="contact">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Aucun message
              </CardContent>
            </Card>
          ) : (
            messages.map((msg) => (
              <Card key={msg.message_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="space-y-1">
                      <p className="font-semibold text-lg">{msg.name}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <a href={`mailto:${msg.email}`} className="text-primary hover:underline flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {msg.email}
                        </a>
                        {msg.phone && (
                          <a href={`tel:${msg.phone}`} className="text-green-600 hover:underline flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            {msg.phone}
                          </a>
                        )}
                      </div>
                    </div>
                    <Badge variant={msg.status === 'new' ? "default" : "secondary"}>
                      {msg.status === 'new' ? 'Nouveau' : msg.status}
                    </Badge>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-lg p-4 mb-3">
                    <p className="font-semibold text-primary mb-2">{msg.subject}</p>
                    <p className="text-foreground whitespace-pre-wrap">{msg.message}</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Reçu le {new Date(msg.created_at).toLocaleString('fr-FR')}
                    </p>
                    {msg.phone && (
                      <a 
                        href={`https://wa.me/${msg.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Répondre sur WhatsApp
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="partnership">
        <div className="space-y-4">
          {partnerships.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Aucune demande
              </CardContent>
            </Card>
          ) : (
            partnerships.map((req) => (
              <Card key={req.request_id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{req.company_name}</p>
                      <p className="text-sm">{req.contact_name} - {req.email}</p>
                      <p className="text-sm text-muted-foreground">{req.phone}</p>
                    </div>
                    <Badge>{req.partnership_type}</Badge>
                  </div>
                  <p className="text-muted-foreground mt-2">{req.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(req.created_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
};

// Blog Management
const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    cover_image: '',
    tags: '',
    is_published: false
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const res = await getBlogPosts(false);
      setPosts(res.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      if (editingPost) {
        await updateBlogPost(editingPost.post_id, data);
        toast.success('Article mis à jour');
      } else {
        await createBlogPost(data);
        toast.success('Article créé');
      }
      
      setShowForm(false);
      setEditingPost(null);
      setFormData({ title: '', content: '', excerpt: '', cover_image: '', tags: '', is_published: false });
      loadPosts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      cover_image: post.cover_image || '',
      tags: post.tags?.join(', ') || '',
      is_published: post.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await deleteBlogPost(postId);
      toast.success('Article supprimé');
      loadPosts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-xl font-semibold">Gestion du Blog</h2>
        <Button onClick={() => { setShowForm(true); setEditingPost(null); }} className="gap-2">
          <Plus className="h-4 w-4" /> Nouvel article
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingPost ? 'Modifier l\'article' : 'Nouvel article'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label>Extrait</Label>
                <Input value={formData.excerpt} onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Image de couverture (URL)</Label>
                <Input value={formData.cover_image} onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Tags (séparés par des virgules)</Label>
                <Input value={formData.tags} onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Contenu (Markdown supporté)</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} rows={10} required />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="published" checked={formData.is_published} onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))} />
                <Label htmlFor="published">Publier</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit">{editingPost ? 'Mettre à jour' : 'Créer'}</Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingPost(null); }}>Annuler</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {posts.map((post) => (
          <Card key={post.post_id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{post.title}</h3>
                <div className="flex gap-2 mt-1">
                  <Badge variant={post.is_published ? "default" : "secondary"}>
                    {post.is_published ? 'Publié' : 'Brouillon'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{post.views} vues</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(post)}><Edit className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(post.post_id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Ads Management
const AdsManagement = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    position: 'sidebar',
    is_active: true
  });

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      const res = await getAdminAds();
      setAds(res.data);
    } catch (error) {
      console.error('Error loading ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createAd(formData);
      toast.success('Publicité créée');
      setShowForm(false);
      setFormData({ title: '', description: '', image_url: '', link_url: '', position: 'sidebar', is_active: true });
      loadAds();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (adId) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    try {
      await deleteAd(adId);
      toast.success('Publicité supprimée');
      loadAds();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-xl font-semibold">Gestion des Publicités</h2>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Nouvelle pub
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nouvelle publicité</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre</Label>
                  <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required />
                </div>
                <div className="space-y-2">
                  <Label>Position</Label>
                  <select className="w-full p-2 border rounded" value={formData.position} onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}>
                    <option value="sidebar">Sidebar</option>
                    <option value="inline">Inline</option>
                    <option value="hero">Hero</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>URL Image</Label>
                <Input value={formData.image_url} onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label>URL de redirection</Label>
                <Input value={formData.link_url} onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))} required />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active" checked={formData.is_active} onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))} />
                <Label htmlFor="active">Active</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit">Créer</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {ads.map((ad) => (
          <Card key={ad.ad_id}>
            <CardContent className="p-4">
              <div className="flex gap-4">
                <img src={ad.image_url} alt={ad.title} className="w-20 h-20 rounded object-cover" />
                <div className="flex-1">
                  <h3 className="font-semibold">{ad.title}</h3>
                  <div className="flex gap-2 mt-1">
                    <Badge variant={ad.is_active ? "default" : "secondary"}>{ad.is_active ? 'Active' : 'Inactive'}</Badge>
                    <Badge variant="outline">{ad.position}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{ad.clicks} clics · {ad.impressions} impressions</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(ad.ad_id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Backup Management
// Security View - Audit and Production Prep
const SecurityView = () => {
  const [auditResult, setAuditResult] = useState(null);
  const [loading, setLoading] = useState('');
  const [productionResult, setProductionResult] = useState(null);

  const handleSecurityAudit = async () => {
    setLoading('audit');
    try {
      const res = await runSecurityAudit();
      setAuditResult(res.data);
      toast.success('Audit de sécurité terminé');
    } catch (error) {
      toast.error('Erreur lors de l\'audit');
    } finally {
      setLoading('');
    }
  };

  const handleCleanupTestData = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer toutes les données de test ?')) {
      return;
    }
    setLoading('cleanup');
    try {
      const res = await cleanupTestData();
      toast.success(`Nettoyage terminé: ${res.data.deleted_sample_listings} annonces sample, ${res.data.deleted_test_title_listings} annonces test supprimées`);
      setAuditResult(null); // Reset audit to re-run
    } catch (error) {
      toast.error('Erreur lors du nettoyage');
    } finally {
      setLoading('');
    }
  };

  const handlePrepareProduction = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir préparer la plateforme pour la production ? Cela supprimera toutes les données de test.')) {
      return;
    }
    setLoading('production');
    try {
      const res = await prepareProduction();
      setProductionResult(res.data);
      toast.success('Plateforme prête pour la production !');
    } catch (error) {
      toast.error('Erreur lors de la préparation');
    } finally {
      setLoading('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Sécurité & Production
          </h2>
          <p className="text-muted-foreground text-sm">Audit de sécurité et préparation au lancement</p>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Security Audit */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/10 flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="font-heading font-semibold mb-2">Audit de Sécurité</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Vérifiez les problèmes de sécurité potentiels
            </p>
            <Button onClick={handleSecurityAudit} disabled={loading === 'audit'} className="w-full gap-2">
              {loading === 'audit' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
              Lancer l'Audit
            </Button>
          </CardContent>
        </Card>

        {/* Cleanup Test Data */}
        <Card className="hover:shadow-lg transition-shadow border-yellow-200">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-yellow-500/10 flex items-center justify-center">
              <Trash2 className="h-8 w-8 text-yellow-600" />
            </div>
            <h3 className="font-heading font-semibold mb-2">Nettoyer Données Test</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Supprimer les annonces sample et test
            </p>
            <Button onClick={handleCleanupTestData} disabled={loading === 'cleanup'} variant="outline" className="w-full gap-2 border-yellow-300 text-yellow-700 hover:bg-yellow-50">
              {loading === 'cleanup' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Nettoyer
            </Button>
          </CardContent>
        </Card>

        {/* Prepare Production */}
        <Card className="hover:shadow-lg transition-shadow border-green-200 bg-gradient-to-br from-green-50/50 to-white">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-500/10 flex items-center justify-center">
              <Rocket className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-heading font-semibold mb-2">Préparer Production</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Préparer le site pour le lancement public
            </p>
            <Button onClick={handlePrepareProduction} disabled={loading === 'production'} className="w-full gap-2 bg-green-600 hover:bg-green-700">
              {loading === 'production' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              Préparer
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Audit Results */}
      {auditResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {auditResult.status === 'PASSED' && <CheckCircle className="h-5 w-5 text-green-500" />}
              {auditResult.status === 'WARNING' && <AlertTriangle className="h-5 w-5 text-yellow-500" />}
              {auditResult.status === 'CRITICAL' && <XCircle className="h-5 w-5 text-red-500" />}
              Résultat de l'Audit
              <Badge variant={auditResult.status === 'PASSED' ? 'default' : auditResult.status === 'WARNING' ? 'secondary' : 'destructive'}>
                {auditResult.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Issues */}
            {auditResult.issues?.length > 0 && (
              <div>
                <h4 className="font-semibold text-red-600 flex items-center gap-2 mb-2">
                  <XCircle className="h-4 w-4" /> Problèmes Critiques
                </h4>
                <ul className="space-y-1">
                  {auditResult.issues.map((issue, i) => (
                    <li key={i} className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded">{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {auditResult.warnings?.length > 0 && (
              <div>
                <h4 className="font-semibold text-yellow-600 flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4" /> Avertissements
                </h4>
                <ul className="space-y-1">
                  {auditResult.warnings.map((warning, i) => (
                    <li key={i} className="text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded">{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Passed */}
            {auditResult.passed?.length > 0 && (
              <div>
                <h4 className="font-semibold text-green-600 flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4" /> Vérifications Réussies
                </h4>
                <ul className="space-y-1">
                  {auditResult.passed.map((item, i) => (
                    <li key={i} className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded flex items-center gap-2">
                      <CheckCircle className="h-3 w-3" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{auditResult.statistics?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground">Utilisateurs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{auditResult.statistics?.google_users || 0}</p>
                <p className="text-xs text-muted-foreground">Google Auth</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{auditResult.statistics?.email_users || 0}</p>
                <p className="text-xs text-muted-foreground">Email Auth</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{auditResult.statistics?.total_listings || 0}</p>
                <p className="text-xs text-muted-foreground">Annonces</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Production Result */}
      {productionResult && (
        <Card className="border-green-300 bg-green-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Rocket className="h-5 w-5" />
              Plateforme Prête !
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Actions effectuées:</h4>
              <ul className="space-y-1">
                {productionResult.actions_taken?.map((action, i) => (
                  <li key={i} className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" /> {action}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Checklist:</h4>
              <ul className="space-y-1">
                {productionResult.checklist?.map((item, i) => (
                  <li key={i} className="text-sm">{item}</li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-2xl font-bold">{productionResult.final_statistics?.users || 0}</p>
                <p className="text-xs text-muted-foreground">Utilisateurs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{productionResult.final_statistics?.listings || 0}</p>
                <p className="text-xs text-muted-foreground">Annonces</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{productionResult.final_statistics?.active_listings || 0}</p>
                <p className="text-xs text-muted-foreground">Actives</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{productionResult.final_statistics?.pending_listings || 0}</p>
                <p className="text-xs text-muted-foreground">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Google OAuth Configuration Reminder */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader>
          <CardTitle className="text-blue-700">Configuration Google OAuth pour roomia.net</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Avant de passer en production sur roomia.net, assurez-vous d'ajouter ces URIs dans la console Google Cloud :
          </p>
          <div className="bg-white p-4 rounded-lg border space-y-2 font-mono text-sm">
            <p><strong>Authorized JavaScript origins:</strong></p>
            <code className="block bg-gray-100 p-2 rounded">https://www.roomia.net</code>
            <code className="block bg-gray-100 p-2 rounded">https://roomia.net</code>
            <p className="mt-4"><strong>Authorized redirect URIs:</strong></p>
            <code className="block bg-gray-100 p-2 rounded">https://www.roomia.net/api/auth/callback/google</code>
            <code className="block bg-gray-100 p-2 rounded">https://roomia.net/api/auth/callback/google</code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const BackupManagement = () => {
  const [loading, setLoading] = useState('');
  const [restoreFile, setRestoreFile] = useState(null);

  const handleExportPdf = async () => {
    setLoading('pdf');
    try {
      const response = await exportUsersPdf();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roomia_users_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setLoading('');
    }
  };

  const handleExportBackup = async () => {
    setLoading('backup');
    try {
      const response = await exportFullBackup();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roomia_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup complet téléchargé');
    } catch (error) {
      toast.error('Erreur lors du backup');
    } finally {
      setLoading('');
    }
  };

  const handleExportImages = async () => {
    setLoading('images');
    try {
      const response = await exportImagesZip();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roomia_images_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Images téléchargées');
    } catch (error) {
      toast.error('Erreur lors de l\'export des images');
    } finally {
      setLoading('');
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error('Sélectionnez un fichier de backup');
      return;
    }
    
    if (!window.confirm('Êtes-vous sûr de vouloir restaurer ce backup ? Les données existantes ne seront pas écrasées.')) {
      return;
    }

    setLoading('restore');
    try {
      const response = await restoreBackup(restoreFile);
      toast.success(`Backup restauré: ${JSON.stringify(response.data.restored)}`);
      setRestoreFile(null);
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    } finally {
      setLoading('');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-xl font-semibold">Sauvegarde & Export</h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Export Users PDF */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FileDown className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-heading font-semibold mb-2">Export Utilisateurs</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Téléchargez la liste des utilisateurs en PDF avec leurs identifiants
            </p>
            <Button onClick={handleExportPdf} disabled={loading === 'pdf'} className="w-full gap-2">
              {loading === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Exporter PDF
            </Button>
          </CardContent>
        </Card>

        {/* Full Backup */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-500/10 flex items-center justify-center">
              <Database className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="font-heading font-semibold mb-2">Backup Complet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Sauvegardez toutes les données (utilisateurs, annonces, blog, etc.) en JSON
            </p>
            <Button onClick={handleExportBackup} disabled={loading === 'backup'} variant="outline" className="w-full gap-2">
              {loading === 'backup' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              Télécharger Backup
            </Button>
          </CardContent>
        </Card>

        {/* Export Images */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/10 flex items-center justify-center">
              <Image className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="font-heading font-semibold mb-2">Export Images</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Téléchargez toutes les images organisées par logement (ZIP)
            </p>
            <Button onClick={handleExportImages} disabled={loading === 'images'} variant="outline" className="w-full gap-2">
              {loading === 'images' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
              Télécharger ZIP
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Restore Section */}
      <Card className="border-2 border-dashed border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Restaurer un Backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Importez un fichier de backup JSON pour restaurer les données. Les données existantes ne seront pas écrasées (seules les nouvelles entrées seront ajoutées).
          </p>
          <div className="flex gap-4 items-center">
            <input
              type="file"
              accept=".json"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              className="flex-1 p-2 border rounded"
            />
            <Button onClick={handleRestore} disabled={!restoreFile || loading === 'restore'} className="gap-2">
              {loading === 'restore' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Restaurer
            </Button>
          </div>
          {restoreFile && (
            <p className="text-sm text-muted-foreground mt-2">
              Fichier sélectionné: {restoreFile.name}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const NeighborhoodsView = () => {
  const [pending, setPending] = useState([]);
  const [cities, setCities] = useState([]);
  const [allByCity, setAllByCity] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pendingRes, citiesRes] = await Promise.all([
        getPendingNeighborhoods(),
        getCities(),
      ]);
      setPending(pendingRes.data || []);
      const cityList = citiesRes.data || [];
      setCities(cityList);
      // Fetch approved neighborhoods per city
      const byCity = {};
      await Promise.all(cityList.map(async (c) => {
        try {
          const res = await getNeighborhoods(c.city_id);
          byCity[c.city_id] = res.data || [];
        } catch (e) { byCity[c.city_id] = []; }
      }));
      setAllByCity(byCity);
    } catch (e) {
      toast.error('Erreur lors du chargement des quartiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await approveNeighborhood(id);
      toast.success('Quartier approuvé');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Refuser et supprimer ce quartier ?')) return;
    setActionLoading(id);
    try {
      await rejectNeighborhood(id);
      toast.success('Quartier refusé');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce quartier ?')) return;
    setActionLoading(id);
    try {
      await deleteNeighborhood(id);
      toast.success('Quartier supprimé');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setActionLoading(null); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!selectedCityId) { toast.error('Sélectionnez une ville'); return; }
    if (!newName.trim() || newName.trim().length < 2) { toast.error('Nom trop court'); return; }
    setAdding(true);
    try {
      await createNeighborhood({ name: newName.trim(), city_id: selectedCityId, status: 'approved' });
      toast.success('Quartier ajouté');
      setNewName('');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setAdding(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
  );

  return (
    <div className="space-y-8" data-testid="neighborhoods-view">
      {/* Pending Proposals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            Quartiers proposés en attente <Badge variant="secondary" data-testid="pending-count">{pending.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun quartier en attente.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Proposé le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((n) => (
                  <TableRow key={n.neighborhood_id} data-testid={`pending-${n.neighborhood_id}`}>
                    <TableCell className="font-medium">{n.name}</TableCell>
                    <TableCell>{n.city_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {n.proposed_at ? new Date(n.proposed_at).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => handleApprove(n.neighborhood_id)}
                          disabled={actionLoading === n.neighborhood_id}
                          data-testid={`approve-neighborhood-${n.neighborhood_id}`}
                        >
                          {actionLoading === n.neighborhood_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
                          Approuver
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleReject(n.neighborhood_id)}
                          disabled={actionLoading === n.neighborhood_id}
                          data-testid={`reject-neighborhood-${n.neighborhood_id}`}
                        >
                          <XCircle className="h-4 w-4" /> Refuser
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add manually */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> Ajouter un quartier manuellement</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex flex-col md:flex-row gap-3">
            <Select value={selectedCityId} onValueChange={setSelectedCityId}>
              <SelectTrigger className="md:w-64" data-testid="admin-city-select">
                <SelectValue placeholder="Ville" />
              </SelectTrigger>
              <SelectContent>
                {cities.map(c => <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="Nom du quartier"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="admin-new-neighborhood-input"
              className="flex-1"
            />
            <Button type="submit" disabled={adding} data-testid="admin-add-neighborhood-btn">
              {adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Ajouter
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Approved per city */}
      <Card>
        <CardHeader>
          <CardTitle>Quartiers approuvés par ville</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {cities.map(c => (
            <div key={c.city_id}>
              <h3 className="font-semibold mb-2">{c.name} <Badge variant="outline">{(allByCity[c.city_id] || []).length}</Badge></h3>
              <div className="flex flex-wrap gap-2">
                {(allByCity[c.city_id] || []).map(n => (
                  <Badge key={n.neighborhood_id} variant="secondary" className="gap-1 pr-1" data-testid={`approved-${n.neighborhood_id}`}>
                    {n.name}
                    <button
                      type="button"
                      className="ml-1 rounded hover:bg-red-100 px-1"
                      onClick={() => handleDelete(n.neighborhood_id)}
                      title="Supprimer"
                      data-testid={`delete-neighborhood-${n.neighborhood_id}`}
                    >
                      <XCircle className="h-3 w-3 text-red-600" />
                    </button>
                  </Badge>
                ))}
                {(allByCity[c.city_id] || []).length === 0 && <span className="text-sm text-muted-foreground">Aucun quartier</span>}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};


export default AdminDashboardPage;
